import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';

const LINK_PATTERN = /\[([^\]]+)\]\(([^)]+)\)/g;

/**
 * Lets an admin turn a specific word or phrase inside an article/news/
 * scientific-content body into a link to another page on the site — e.g.
 * writing `[کلسیت](/mineralogy/calcite)` inside an article's body makes the
 * word "کلسیت" a clickable link to the calcite mineralogy entry.
 *
 * Deliberately a small regex-based parser for one syntax, not a markdown
 * library — no new dependency, and this is the one formatting need that was
 * actually asked for. Internal links (paths starting with '/') render as
 * React Router `<Link>` for client-side navigation; anything else (a full
 * `https://...` URL) renders as a normal external link that opens in a new
 * tab. Plain text with no `[label](url)` pattern in it passes through
 * unchanged — this is safe to run on every paragraph unconditionally.
 */
export function renderTextWithLinks(text: string, keyPrefix: string): ReactNode[] {
  const parts: ReactNode[] = [];
  let lastIndex = 0;
  let matchIndex = 0;
  let match: RegExpExecArray | null;

  LINK_PATTERN.lastIndex = 0;
  while ((match = LINK_PATTERN.exec(text)) !== null) {
    const [fullMatch, label, url] = match;

    if (match.index > lastIndex) {
      parts.push(text.slice(lastIndex, match.index));
    }

    if (url.startsWith('/')) {
      parts.push(
        <Link key={`${keyPrefix}-${matchIndex}`} to={url} className="dma-inline-link">
          {label}
        </Link>
      );
    } else {
      parts.push(
        <a
          key={`${keyPrefix}-${matchIndex}`}
          href={url}
          target="_blank"
          rel="noreferrer"
          className="dma-inline-link"
        >
          {label}
        </a>
      );
    }

    lastIndex = match.index + fullMatch.length;
    matchIndex += 1;
  }

  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex));
  }

  return parts;
}
